import React, { useState, useEffect } from "react";
import {
  Users,
  Download,
  FileText,
  CheckCircle,
  XCircle,
  Eye,
  GitCompare,
  Clock,
  ArrowUpDown,
  Star,
  Bookmark,
  Target,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  getDocs,
  addDoc,
} from "firebase/firestore";
import toast from "react-hot-toast";

// Import new components
import CandidateFilters from "../components/CandidateFilters";
import ResumePreviewModal from "../components/ResumePreviewModal";
import CandidateComparison from "../components/CandidateComparison";
import VerifiedBadge from "../../components/VerifiedBadge";

// Import utilities
import {
  calculateSkillsMatch,
  extractSkillsFromJD,
  formatTimeline,
  formatDaysAgo,
  exportToCSV,
  filterCandidates,
  sortCandidates,
} from "../utils/candidateUtils";

const CandidateManagement = () => {
  const { user } = useAuth();
  const db = getFirestore(firebaseApp);

  // State
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedCandidates, setSavedCandidates] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");

  // Modals
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeModalData, setResumeModalData] = useState(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);

  // Filters
  const [filters, setFilters] = useState({});
  const [selectedJob, setSelectedJob] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");

  // UI State - context panel removed for this page  user request

  // Fetch saved candidates
  useEffect(() => {
    if (!user) return;

    const fetchSavedCandidates = async () => {
      try {
        const savedDoc = await getDoc(doc(db, "hrSavedCandidates", user.id));
        if (savedDoc.exists()) {
          const data = savedDoc.data();
          setSavedCandidates(data.candidateIds || []);
        }
      } catch (error) {
        console.error("Error fetching saved candidates:", error);
      }
    };

    fetchSavedCandidates();
  }, [user, db]);

  // Fetch candidates data
  useEffect(() => {
    if (!user) return;

    const setupListeners = async () => {
      try {
        // Get all tests created by this HR
        const hrTestsQuery = query(
          collection(db, "hrTests"),
          where("hrId", "==", user.id)
        );

        const unsubscribeTests = onSnapshot(hrTestsQuery, (hrTestsSnapshot) => {
          const hrTestIds = hrTestsSnapshot.docs.map((doc) => doc.id);

          if (hrTestIds.length === 0) {
            // Try direct query 
            const directResultsQuery = query(
              collection(db, "jobseekerResults"),
              where("hrId", "==", user.id),
              orderBy("completedAt", "desc")
            );

            const unsubscribeDirectResults = onSnapshot(
              directResultsQuery,
              async (directSnapshot) => {
                await processResults(directSnapshot.docs);
              }
            );

            return () => unsubscribeDirectResults();
          }

          // Query results for HR's tests
          const resultsQuery = query(
            collection(db, "jobseekerResults"),
            where("testId", "in", hrTestIds),
            orderBy("completedAt", "desc")
          );

          const unsubscribeResults = onSnapshot(resultsQuery, async (resultsSnapshot) => {
            await processResults(resultsSnapshot.docs);
          });

          return () => unsubscribeResults();
        });

        return () => unsubscribeTests();
      } catch (error) {
        console.error("Error setting up listeners:", error);
        setLoading(false);
      }
    };

    const processResults = async (resultsDocs) => {
      try {
        const results = resultsDocs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch user profiles and shortlist/reject status for enrichment
        const enrichedCandidates = await Promise.all(
          results.map(async (result) => {
            let profileData = {};
            let shortlistedData = null;
            let rejectedData = null;

            // Fetch profile data
            try {
              const profileDoc = await getDoc(
                doc(db, "userProfiles", result.jobSeekerId)
              );
              if (profileDoc.exists()) {
                profileData = profileDoc.data();
              }
            } catch (err) {
              console.warn("Could not fetch profile for:", result.jobSeekerId);
            }

            // Check if shortlisted
            try {
              const shortlistedQuery = query(
                collection(db, "hrShortlisted"),
                where("hrId", "==", user.id),
                where("candidateId", "==", result.jobSeekerId)
              );
              const shortlistedSnapshot = await getDocs(shortlistedQuery);
              if (!shortlistedSnapshot.empty) {
                shortlistedData = {
                  id: shortlistedSnapshot.docs[0].id,
                  ...shortlistedSnapshot.docs[0].data(),
                };
              }
            } catch (err) {
              // Ignore
            }

            // Check if rejected
            try {
              const rejectedQuery = query(
                collection(db, "hrRejected"),
                where("hrId", "==", user.id),
                where("candidateId", "==", result.jobSeekerId)
              );
              const rejectedSnapshot = await getDocs(rejectedQuery);
              if (!rejectedSnapshot.empty) {
                rejectedData = {
                  id: rejectedSnapshot.docs[0].id,
                  ...rejectedSnapshot.docs[0].data(),
                };
              }
            } catch (err) {
              // Ignore
            }

            // Calculate skills match if job is selected
            let skillsMatchPercentage = undefined;
            if (filters.jobId && selectedJob) {
              const requiredSkills = extractSkillsFromJD(selectedJob.jobDescription || "");
              const candidateSkills =
                profileData.resumeData?.professionalInfo?.skills || result.skills || [];
              skillsMatchPercentage = calculateSkillsMatch(
                candidateSkills,
                requiredSkills
              );
            }

            // Format timeline
            const timeline = formatTimeline({
              completedAt: result.completedAt,
              shortlistedAt: shortlistedData?.shortlistedAt,
              rejectedAt: rejectedData?.rejectedAt,
            });

            return {
              id: result.jobSeekerId || result.id,
              resultId: result.id,
              name: result.jobSeekerName || "Unknown Candidate",
              email: result.jobSeekerEmail || "No email provided",
              position: result.jobTitle || "Unknown Position",
              avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(
                result.jobSeekerName || "Unknown"
              )}&background=ef4444&color=fff`,
              status: result.hasCompletedInterview
                ? result.recommendationForHiring === "STRONG RECOMMEND" ||
                  result.recommendationForHiring === "RECOMMEND"
                  ? "interview_completed_recommended"
                  : "interview_completed"
                : result.qualifiedForInterview === false
                  ? "test_failed"
                  : "test_completed",
              mcqScore: result.mcqScore || 0,
              interviewScore: result.interviewScore || 0,
              totalScore: result.totalScore || result.mcqScore || 0,
              hasCompletedInterview: result.hasCompletedInterview || false,
              qualifiedForInterview: result.qualifiedForInterview !== false,
              recommendationForHiring: result.recommendationForHiring || "NEUTRAL",
              keyFindings: result.keyFindings || "",
              skills:
                profileData.resumeData?.professionalInfo?.skills || result.skills || [],
              experience:
                profileData.resumeData?.professionalInfo?.experience || "Not specified",
              location: result.location || "Not specified",
              appliedDate:
                result.completedAt?.toDate?.()?.toLocaleDateString() ||
                new Date().toLocaleDateString(),
              completedAt: result.completedAt,
              skillsVerified: profileData.skillsVerified || false,
              skillsMatchPercentage,
              timeline,
              isShortlisted: !!shortlistedData,
              isRejected: !!rejectedData,
              shortlistedData,
              rejectedData,
              testId: result.testId,
              testTitle: result.testTitle,
            };
          })
        );

        // Remove duplicates (keep latest)
        const uniqueCandidates = enrichedCandidates.reduce(
          (acc, current) => {
            const existing = acc.find((item) => item.id === current.id);
            if (
              !existing ||
              new Date(current.appliedDate) > new Date(existing.appliedDate)
            ) {
              return [...acc.filter((item) => item.id !== current.id), current];
            }
            return acc;
          },
          []
        );

        setCandidates(uniqueCandidates);
        setLoading(false);
      } catch (error) {
        console.error("Error processing candidates:", error);
        setCandidates([]);
        setLoading(false);
      }
    };

    setupListeners();
  }, [user, db, filters.jobId, selectedJob]);

  // Apply filters and sorting
  useEffect(() => {
    let result = filterCandidates(candidates, filters);
    if (statusFilter === "shortlisted") {
      result = result.filter((c) => c.isShortlisted);
    } else if (statusFilter === "rejected") {
      result = result.filter((c) => c.isRejected);
    }
    result = sortCandidates(result, sortBy, sortDirection);
    setFilteredCandidates(result);
  }, [candidates, filters, sortBy, sortDirection, statusFilter]);

  // Fetch selected job when filter changes
  useEffect(() => {
    if (!filters.jobId) {
      setSelectedJob(null);
      return;
    }

    const fetchJob = async () => {
      try {
        const jobDoc = await getDoc(doc(db, "hrJobs", filters.jobId));
        if (jobDoc.exists()) {
          setSelectedJob({ id: jobDoc.id, ...jobDoc.data() });
        }
      } catch (error) {
        console.error("Error fetching job:", error);
      }
    };

    fetchJob();
  }, [filters.jobId, db]);

  // Handlers
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates((prev) =>
      prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCandidates.length === filteredCandidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(filteredCandidates.map((c) => c.id));
    }
  };

  const handleSaveCandidate = async (candidateId) => {
    if (!user) return;

    try {
      const isCurrentlySaved = savedCandidates.includes(candidateId);
      const newSavedCandidates = isCurrentlySaved
        ? savedCandidates.filter((id) => id !== candidateId)
        : [...savedCandidates, candidateId];

      await setDoc(doc(db, "hrSavedCandidates", user.id), {
        candidateIds: newSavedCandidates,
        hrId: user.id,
        lastUpdated: new Date(),
      });

      setSavedCandidates(newSavedCandidates);
      toast.success(
        isCurrentlySaved ? "Removed from saved" : "Candidate saved!"
      );
    } catch (error) {
      console.error("Error saving candidate:", error);
      toast.error("Failed to save candidate");
    }
  };

  const handleViewResume = (candidate) => {
    setResumeModalData({ candidateId: candidate.id, candidateName: candidate.name });
    setShowResumeModal(true);
  };

  const handleCompare = () => {
    if (selectedCandidates.length < 2 || selectedCandidates.length > 3) {
      toast.error("Please select 2-3 candidates to compare");
      return;
    }

    setShowComparisonModal(true);
  };

  const handleBulkShortlist = async () => {
    if (selectedCandidates.length === 0) {
      toast.error("Please select candidates first");
      return;
    }

    try {
      const promises = selectedCandidates.map(async (candidateId) => {
        const candidate = filteredCandidates.find((c) => c.id === candidateId);
        if (!candidate) return;

        await addDoc(collection(db, "hrShortlisted"), {
          hrId: user?.id,
          candidateId: candidate.id,
          candidateName: candidate.name || "Unknown",
          candidateEmail: candidate.email || "",
          testId: candidate.testId || "",
          testTitle: candidate.testTitle || "Unknown Test",
          totalScore: candidate.totalScore || 0,
          recommendationForHiring: candidate.recommendationForHiring || "NEUTRAL",
          shortlistedAt: new Date(),
        });
      });

      await Promise.all(promises);
      setSelectedCandidates([]);
      toast.success(`Shortlisted ${selectedCandidates.length} candidates!`);
    } catch (error) {
      console.error("Error bulk shortlisting:", error);
      toast.error("Failed to shortlist candidates");
    }
  };

  const handleBulkReject = async () => {
    if (selectedCandidates.length === 0) {
      toast.error("Please select candidates first");
      return;
    }

    try {
      const promises = selectedCandidates.map(async (candidateId) => {
        const candidate = filteredCandidates.find((c) => c.id === candidateId);
        if (!candidate) return;

        await addDoc(collection(db, "hrRejected"), {
          hrId: user?.id,
          candidateId: candidate.id,
          candidateName: candidate.name || "Unknown",
          candidateEmail: candidate.email || "",
          testId: candidate.testId || "",
          testTitle: candidate.testTitle || "Unknown Test",
          totalScore: candidate.totalScore || 0,
          recommendationForHiring: candidate.recommendationForHiring || "NEUTRAL",
          rejectedAt: new Date(),
        });
      });

      await Promise.all(promises);
      setSelectedCandidates([]);
      toast.success(`Rejected ${selectedCandidates.length} candidates`);
    } catch (error) {
      console.error("Error bulk rejecting:", error);
      toast.error("Failed to reject candidates");
    }
  };

  const handleExportCSV = () => {
    exportToCSV(filteredCandidates, `candidates_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Candidates exported to CSV!");
  };

  const toggleSort = (criteria) => {
    if (sortBy === criteria) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(criteria);
      setSortDirection("desc");
    }
  };

  // Get top 5 AI recommended candidates
  const topRecommended = filteredCandidates
    .filter(
      (c) =>
        c.recommendationForHiring === "STRONG RECOMMEND" ||
        c.recommendationForHiring === "RECOMMEND"
    )
    .slice(0, 5);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading candidates...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Users className="w-8 h-8 mr-3 text-red-600" />
            Candidate Management
          </h1>
          <p className="text-gray-600 mt-1">
            Advanced candidate screening and management
          </p>
        </div>
      </div>

      {/* Main Layout - Stacked */}
      <div className="flex flex-col gap-6">

        {/* Filters & Status */}
        <div className="space-y-4">
          <CandidateFilters
            onFilterChange={handleFilterChange}
            candidatesCount={filteredCandidates.length}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={toggleSort}
          />

          {/* Status View Toggles */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {[
              { key: "all", label: "All Candidates", icon: Users },
              { key: "shortlisted", label: "Shortlisted", icon: CheckCircle },
              { key: "rejected", label: "Rejected", icon: XCircle },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setStatusFilter(opt.key)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${statusFilter === opt.key
                  ? "bg-gray-900 text-white shadow-lg shadow-gray-200 transform scale-100 ring-1 ring-gray-900"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:border-gray-300 shadow-sm"
                  }`}
              >
                <opt.icon className="w-4 h-4" />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="space-y-6">
          {/* Top 5 AI Recommended */}
          {topRecommended.length > 0 && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-sm border-2 border-green-300 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <Star className="w-5 h-5 text-yellow-500 mr-2" />
                Top 5 AI Recommended Candidates
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 overflow-hidden">
                {topRecommended.map((candidate) => (
                  <div
                    key={candidate.id}
                    className="bg-white rounded-lg p-3 border border-green-200 hover:border-green-400 transition-all cursor-pointer overflow-hidden"
                    onClick={() => handleViewResume(candidate)}
                  >
                    <div className="flex items-center space-x-2 mb-2 overflow-hidden">
                      <img
                        src={candidate.avatar}
                        alt={candidate.name}
                        className="w-10 h-10 rounded-full flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="font-semibold text-gray-900 text-sm truncate">
                          {candidate.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {candidate.position}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-lg font-bold text-green-600 flex-shrink-0">
                        {candidate.totalScore?.toFixed(0)}%
                      </span>
                      {candidate.skillsMatchPercentage !== undefined && (
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full whitespace-nowrap">
                          <Target className="w-3 h-3 inline mr-1" />
                          {candidate.skillsMatchPercentage}% match
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  checked={
                    selectedCandidates.length === filteredCandidates.length &&
                    filteredCandidates.length > 0
                  }
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-600">
                  {selectedCandidates.length > 0
                    ? `${selectedCandidates.length} selected`
                    : `${filteredCandidates.length} candidates`}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {selectedCandidates.length > 0 && (
                  <>
                    <button
                      onClick={handleBulkShortlist}
                      className="px-3 py-1.5 bg-green-100 text-green-800 text-sm rounded-lg hover:bg-green-200 transition-colors flex items-center"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Shortlist
                    </button>
                    <button
                      onClick={handleBulkReject}
                      className="px-3 py-1.5 bg-red-100 text-red-800 text-sm rounded-lg hover:bg-red-200 transition-colors flex items-center"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </button>
                    {selectedCandidates.length >= 2 && selectedCandidates.length <= 3 && (
                      <button
                        onClick={handleCompare}
                        className="px-3 py-1.5 bg-blue-100 text-blue-800 text-sm rounded-lg hover:bg-blue-200 transition-colors flex items-center"
                      >
                        <GitCompare className="w-4 h-4 mr-1" />
                        Compare
                      </button>
                    )}
                  </>
                )}
                <button
                  onClick={handleExportCSV}
                  className="px-3 py-1.5 bg-gray-100 text-gray-800 text-sm rounded-lg hover:bg-gray-200 transition-colors flex items-center"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Export CSV
                </button>
              </div>
            </div>
          </div>

          {/* Candidates Grid */}
          {filteredCandidates.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="text-gray-500">
                {candidates.length === 0 ? (
                  <div>
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-lg font-semibold text-gray-900 mb-2">No candidates yet</p>
                    <p className="text-sm text-gray-500">
                      Candidates will appear here after they complete your tests.
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Target className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-lg font-semibold text-gray-900">No candidates match</p>
                    <p className="text-sm text-gray-500 mt-1">Try adjusting your filters</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCandidates.map((candidate) => (
                <div
                  key={candidate.id}
                  className={`bg-white rounded-2xl p-5 transition-all duration-300 relative group ${savedCandidates.includes(candidate.id)
                    ? "border-2 border-red-100 shadow-card-hover ring-2 ring-red-50"
                    : candidate.isShortlisted
                      ? "border border-green-200 shadow-sm bg-gradient-to-br from-white to-green-50/30 hover:shadow-lg"
                      : candidate.isRejected
                        ? "border border-gray-200 opacity-75 grayscale-[0.5] hover:grayscale-0 hover:opacity-100 bg-gray-50"
                        : "border border-gray-200 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-red-100"
                    }`}
                >
                  {/* Selection Checkbox */}
                  <div className="absolute top-4 left-4 z-10">
                    <input
                      type="checkbox"
                      checked={selectedCandidates.includes(candidate.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectCandidate(candidate.id);
                      }}
                      className="w-4 h-4 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer transition-all"
                    />
                  </div>

                  {/* Bookmark Button */}
                  <div className="absolute top-3 right-3 z-10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveCandidate(candidate.id);
                      }}
                      className={`p-1.5 rounded-lg transition-all ${savedCandidates.includes(candidate.id)
                        ? "bg-red-50 text-red-600 shadow-sm"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        }`}
                    >
                      <Bookmark
                        className={`w-4 h-4 ${savedCandidates.includes(candidate.id) ? "fill-current" : ""
                          }`}
                      />
                    </button>
                  </div>

                  {/* Main Content */}
                  <div className="pt-6">
                    {/* Header: Avatar & Info */}
                    <div className="flex items-start gap-3 mb-4">
                      <div className="relative">
                        <img
                          src={candidate.avatar}
                          alt={candidate.name}
                          className="w-12 h-12 rounded-xl object-cover shadow-sm bg-gray-50 border border-gray-100"
                        />
                        {candidate.skillsMatchPercentage !== undefined && (
                          <div className="absolute -bottom-1.5 -right-1.5 bg-white rounded-md shadow border border-gray-100 px-1 py-0.5 flex items-center gap-0.5">
                            <Target className="w-2.5 h-2.5 text-red-500" />
                            <span className="text-[10px] font-bold text-gray-900">{candidate.skillsMatchPercentage}%</span>
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <h3 className="text-base font-bold text-gray-900 truncate">
                            {candidate.name}
                          </h3>
                          <VerifiedBadge isVerified={candidate.skillsVerified} size="sm" />
                        </div>
                        <p className="text-xs text-gray-500 truncate font-medium">{candidate.position}</p>
                        <p className="text-[10px] text-gray-400 truncate mt-0.5">{candidate.email}</p>
                      </div>
                    </div>

                    {/* Scores Grid */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                        <span className="text-[10px] text-gray-500 font-medium block mb-0.5">MCQ Score</span>
                        <span className="text-base font-bold text-gray-900">
                          {candidate.mcqScore.toFixed(0)}<span className="text-[10px] text-gray-400 font-normal">/25</span>
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                        <span className="text-[10px] text-gray-500 font-medium block mb-0.5">Interview</span>
                        <span className="text-base font-bold text-gray-900">
                          {candidate.interviewScore.toFixed(0)}<span className="text-[10px] text-gray-400 font-normal">/100</span>
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-xs pt-3 border-t border-gray-100 mt-3">
                      <span className="font-bold text-gray-900">Total Score</span>
                      <span className="font-bold text-red-600 text-base">
                        {candidate.totalScore.toFixed(0)}%
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewResume(candidate);
                        }}
                        className="flex-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-semibold flex items-center justify-center"
                      >
                        <Eye className="w-3.5 h-3.5 mr-1.5" />
                        Resume
                      </button>

                      {candidate.isShortlisted ? (
                        <div className="flex-1 px-3 py-1.5 bg-green-100 text-green-800 rounded-lg text-xs font-semibold flex items-center justify-center">
                          <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                          Shortlisted
                        </div>
                      ) : candidate.isRejected ? (
                        <div className="flex-1 px-3 py-1.5 bg-gray-100 text-gray-500 rounded-lg text-xs font-semibold flex items-center justify-center opacity-75">
                          <XCircle className="w-3.5 h-3.5 mr-1.5" />
                          Rejected
                        </div>
                      ) : (
                        <div className="flex-1 flex gap-2">
                          {/* Future action buttons could go here */}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showResumeModal && resumeModalData && (
        <ResumePreviewModal
          candidateId={resumeModalData.candidateId}
          candidateName={resumeModalData.candidateName}
          isOpen={showResumeModal}
          onClose={() => {
            setShowResumeModal(false);
            setResumeModalData(null);
          }}
        />
      )}
      {showComparisonModal && (
        <CandidateComparison
          candidates={filteredCandidates.filter((c) =>
            selectedCandidates.includes(c.id)
          )}
          isOpen={showComparisonModal}
          onClose={() => setShowComparisonModal(false)}
        />
      )}
    </div>
  );
};

export default CandidateManagement;
